import React, { useMemo, useState, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  InputAdornment,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import UndoIcon from "@mui/icons-material/Undo";
import RefreshIcon from "@mui/icons-material/Refresh";
import Checkbox from "@mui/material/Checkbox";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const money = (value) =>
  Number(value ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getResponseData = (response) => response?.data?.data ?? response?.data ?? {};

const getAuthHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
  "Content-Type": "application/json",
});

const showAlert = (options) =>
  Swal.fire({
    confirmButtonColor: "#1976d2",
    didOpen: () => {
      Swal.getContainer()?.style.setProperty("z-index", "20000", "important");
    },
    ...options,
  });

export default function FeeReceiptsView({ selectedStudent }) {
  const user = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const rollno =
    selectedStudent?.student_rollno ??
    selectedStudent?.student_roll_no ??
    selectedStudent?.rollno ??
    "";

  const studentName = selectedStudent?.student_name ?? selectedStudent?.name ?? "";

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [printingId, setPrintingId] = useState(null);
  const [revertingId, setRevertingId] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedReceiptIds, setSelectedReceiptIds] = useState([]);
  const [printingSelected, setPrintingSelected] = useState(false);

  const toggleReceiptSelection = (receiptId) => {
    const id = String(receiptId);

    setSelectedReceiptIds((previous) =>
      previous.includes(id) ? previous.filter((item) => item !== id) : [...previous, id]
    );
  };
  const printSelectedReceipts = async () => {
    if (selectedReceiptIds.length === 0) {
      await showAlert({
        title: "Select Receipts",
        text: "Please select at least one receipt.",
        icon: "warning",
      });
      return;
    }

    const printWindow = window.open("about:blank", "_blank");

    if (!printWindow) {
      await showAlert({
        title: "Popup Blocked",
        text: "Allow popups for this site and try again.",
        icon: "warning",
      });
      return;
    }

    setPrintingSelected(true);

    try {
      const url =
        `${API_BASE_URL}/api/feereceipt-multiple/` + `${clientId}/${collegeId}/preview-pdf`;

      const response = await axios.get(url, {
        params: {
          receiptIds: selectedReceiptIds.join(","),
        },
        headers: getAuthHeaders(),
        responseType: "blob",
        timeout: 30000,
      });

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      if (!blob.size) {
        throw new Error("The receipt PDF is empty.");
      }

      const pdfUrl = URL.createObjectURL(blob);
      printWindow.location.href = pdfUrl;

      window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 120000);
    } catch (err) {
      printWindow.close();

      console.error("Selected receipts PDF error:", err);

      await showAlert({
        title: "Print Failed",
        text:
          err?.response?.data?.message ||
          err?.message ||
          "Unable to generate the selected receipts PDF.",
        icon: "error",
      });
    } finally {
      setPrintingSelected(false);
    }
  };
  const toggleSelectAllVisible = (checked) => {
    const visibleIds = filteredReceipts.map((receipt) => String(receipt.fee_receipt_id));

    setSelectedReceiptIds((previous) => {
      if (checked) {
        return [...new Set([...previous, ...visibleIds])];
      }

      return previous.filter((id) => !visibleIds.includes(id));
    });
  };
  const filteredReceipts = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    if (!query) return receipts;

    return receipts.filter((receipt) => {
      const receiptNo = String(
        receipt.college_receipt_no ?? receipt.receipt_no ?? ""
      ).toLowerCase();

      const amount = String(receipt.fee_amount ?? "").toLowerCase();
      const formattedAmount = money(receipt.fee_amount).toLowerCase();

      const rawDate = receipt.receipt_date;
      const parsedDate = rawDate ? new Date(rawDate) : null;

      const formattedDate =
        parsedDate && !Number.isNaN(parsedDate.getTime())
          ? parsedDate.toLocaleDateString("en-IN")
          : String(rawDate ?? "");

      const isoDate =
        parsedDate && !Number.isNaN(parsedDate.getTime())
          ? parsedDate.toISOString().slice(0, 10)
          : "";

      return [receiptNo, amount, formattedAmount, formattedDate.toLowerCase(), isoDate].some(
        (value) => value.includes(query)
      );
    });
  }, [receipts, searchText]);
  const loadReceipts = useCallback(async () => {
    if (!clientId || !rollno) {
      setError("Client, college, or student roll number is missing.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      /*
       * BACKEND CONTRACT:
       * Implement/replace this URL with your actual receipt-list route.
       * Expected response: { data: { receipts: [...] } }
       */
      const studentId = selectedStudent?.student_id;

      const url = `${API_BASE_URL}/api/fee-receipts/` + `${clientId}/student/${studentId}/receipts`;

      const response = await axios.get(url, {
        headers: getAuthHeaders(),
      });

      const result = getResponseData(response);

      const list = Array.isArray(result)
        ? result
        : Array.isArray(result?.receipts)
        ? result.receipts
        : Array.isArray(result?.rows)
        ? result.rows
        : [];

      setReceipts(
        list.map((receipt) => ({
          ...receipt,
          id: receipt.fee_receipt_id,
        }))
      );
      setPage(0);
    } catch (err) {
      console.error("Unable to load student receipts:", err);

      setError(err?.response?.data?.message || err?.message || "Unable to load student receipts.");
    } finally {
      setLoading(false);
    }
  }, [clientId, collegeId, rollno]);

  const openReceipts = async () => {
    if (!rollno) {
      await showAlert({
        title: "Select Student",
        text: "Please search and select a student first.",
        icon: "warning",
      });
      return;
    }

    setOpen(true);
    await loadReceipts();
  };

  const printReceipt = async (receipt) => {
    const receiptId = receipt?.fee_receipt_id;

    if (!receiptId) {
      await showAlert({
        title: "Receipt ID Missing",
        text: "Unable to identify this receipt.",
        icon: "error",
      });
      return;
    }

    // Open synchronously to reduce browser popup blocking.
    const printWindow = window.open("about:blank", "_blank");

    if (!printWindow) {
      await showAlert({
        title: "Popup Blocked",
        text: "Allow popups for this site and click Print again.",
        icon: "warning",
      });
      return;
    }

    setPrintingId(receiptId);

    try {
      const url =
        `${API_BASE_URL}/api/feereceipt-multiple/` + `${clientId}/${collegeId}/preview-pdf`;

      const response = await axios.get(url, {
        params: {
          receiptIds: String(receiptId),
        },
        headers: getAuthHeaders(),
        responseType: "blob",
        timeout: 30000,
      });

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      if (!blob.size) {
        throw new Error("The receipt PDF is empty.");
      }

      const pdfUrl = URL.createObjectURL(blob);
      printWindow.location.href = pdfUrl;

      window.setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 120000);
    } catch (err) {
      printWindow.close();

      console.error("Receipt PDF error:", err);

      await showAlert({
        title: "Print Failed",
        text: err?.response?.data?.message || err?.message || "Unable to generate the receipt PDF.",
        icon: "error",
      });
    } finally {
      setPrintingId(null);
    }
  };

  const revertReceipt = async (receipt) => {
    const receiptId = receipt?.fee_receipt_id;

    if (!receiptId) return;

    const confirmation = await showAlert({
      title: "Revert Receipt?",
      html:
        `Receipt No: <b>${String(receipt.college_receipt_no ?? receiptId)}</b><br/>` +
        `Amount: <b>₹${money(receipt.fee_amount)}</b><br/><br/>` +
        "Please verify the receipt before proceeding.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Revert",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d32f2f",
    });

    if (!confirmation.isConfirmed) return;

    setRevertingId(receiptId);

    try {
      /*
       * BACKEND CONTRACT:
       * Replace this URL and method with your actual receipt-revert API.
       * Reversion must be validated and processed on the server.
       */
      const url =
        `${API_BASE_URL}/api/fee-receipts/` + `${clientId}/${collegeId}/${receiptId}/revert`;

      await axios.post(url, {}, { headers: getAuthHeaders() });

      await showAlert({
        title: "Receipt Reverted",
        text: "The receipt was reverted successfully.",
        icon: "success",
      });

      await loadReceipts();
    } catch (err) {
      console.error("Receipt revert failed:", err);

      await showAlert({
        title: "Revert Failed",
        text: err?.response?.data?.message || err?.message || "Unable to revert this receipt.",
        icon: "error",
      });
    } finally {
      setRevertingId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "select",
        headerName: "",
        width: 55,
        minWidth: 55,
        maxWidth: 55,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => {
          const visibleIds = filteredReceipts.map((r) => String(r.fee_receipt_id));

          const allSelected =
            visibleIds.length > 0 && visibleIds.every((id) => selectedReceiptIds.includes(id));

          const someSelected = visibleIds.some((id) => selectedReceiptIds.includes(id));

          return (
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={!allSelected && someSelected}
              onChange={(e) => toggleSelectAllVisible(e.target.checked)}
            />
          );
        },
        renderCell: (params) => {
          const id = String(params.row.fee_receipt_id);

          return (
            <Checkbox
              size="small"
              checked={selectedReceiptIds.includes(id)}
              onChange={() => toggleReceiptSelection(id)}
            />
          );
        },
      },

      {
        field: "sno",
        headerName: "SNo.",
        width: 70,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const rowIndex = params.api.getRowIndex(params.id);
          return rowIndex >= 0 ? rowIndex + 1 : "";
        },
      },
      {
        field: "fee_head_name",
        headerName: "Fee Head Name",
        minWidth: 180,
        flex: 1.5,
        valueGetter: (params) => params.row?.feeBook?.feeHead?.fee_head_name ?? "",
      },
      {
        field: "college_receipt_no",
        headerName: "Receipt No.",
        minWidth: 120,
        flex: 1,
      },
      {
        field: "receipt_date",
        headerName: "Receipt Date",
        minWidth: 115,
        flex: 1,
        valueFormatter: (params) => {
          if (!params.value) return "";
          const date = new Date(params.value);
          return Number.isNaN(date.getTime())
            ? String(params.value)
            : date.toLocaleDateString("en-IN");
        },
      },
      {
        field: "fee_amount",
        headerName: "Amount (₹)",
        minWidth: 125,
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (params) => money(params.value),
      },
      {
        field: "payment_mode",
        headerName: "Payment Mode",
        minWidth: 120,
        flex: 1,
      },
      {
        field: "trn_ref_no",
        headerName: "Transaction Ref.",
        minWidth: 145,
        flex: 1,
        valueGetter: (params) => params.row?.trn_ref_no || params.row?.qrtno || "—",
      },
      {
        field: "termno",
        headerName: "Term",
        width: 85,
        align: "center",
        headerAlign: "center",
        valueFormatter: (params) => (params.value == null ? "—" : `Term-${params.value}`),
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 165,
        flex: 1,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const receipt = params.row;
          const id = receipt.fee_receipt_id;

          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Print Receipt">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={printingId === id || revertingId === id}
                    onClick={() => printReceipt(receipt)}
                  >
                    {printingId === id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <PrintIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Revert Receipt">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={revertingId === id || printingId === id}
                    onClick={() => revertReceipt(receipt)}
                  >
                    {revertingId === id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <UndoIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [printingId, revertingId, selectedReceiptIds, filteredReceipts]
  );

  return (
    <>
      <Button
        variant="contained"
        fullWidth
        startIcon={<ReceiptLongIcon />}
        disabled={!selectedStudent || loading}
        onClick={openReceipts}
        sx={{
          minHeight: 42,
          color: "#fff",
          backgroundColor: "#1976d2",
          fontWeight: 700,
          fontSize: "0.78rem",
          whiteSpace: "normal",
          lineHeight: 1.3,
          px: 0.75,
          "&:hover": {
            backgroundColor: "#1565c0",
          },
        }}
      >
        Show Receipts
      </Button>

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          setOpen(false);
        }}
        disableEscapeKeyDown
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#3781e2",
            color: "#fff",
            fontWeight: 400,
            pr: 1,
            py: 0.5,
            pl: 2,

            minHeight: "36px",
            boxSizing: "border-box",
          }}
        >
          <Box>STUDENT FEE RECEIPTS</Box>

          <IconButton
            aria-label="Close receipts"
            onClick={() => setOpen(false)}
            sx={{
              color: "#d32f2f",
              backgroundColor: "#ffebee",
              "&:hover": {
                backgroundColor: "#ffebee",
                color: "#b71c1c",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
            sx={{ mb: 2, width: "100%" }}
          >
            {/* Student Details */}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#010b12",
                  mt: 0.3,
                  overflowWrap: "anywhere",
                  lineHeight: 1.6,
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Name:
                </Box>{" "}
                {studentName}
                {"  "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Roll No:
                </Box>{" "}
                {rollno || "-"}
              </Typography>
            </Box>

            {/* Total Receipts */}
            <Typography
              fontWeight={600}
              fontSize={15}
              sx={{
                whiteSpace: { xs: "normal", sm: "nowrap" },
                textAlign: { xs: "left", sm: "center" },
              }}
            >
              Total Receipts: {receipts.length}
            </Typography>

            {/* Action Buttons */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{
                width: { xs: "100%", sm: "auto" },
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadReceipts}
                disabled={loading}
                fullWidth
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  color: "#1976d2",
                  borderColor: "#1976d2",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  minHeight: 40,
                }}
              >
                Refresh
              </Button>

              <Button
                variant="contained"
                startIcon={
                  printingSelected ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PictureAsPdfIcon />
                  )
                }
                onClick={printSelectedReceipts}
                disabled={printingSelected || selectedReceiptIds.length === 0}
                fullWidth
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  color: "#FFFFFF",
                  backgroundColor: "#1976d2",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  minHeight: 40,
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                }}
              >
                Print Selected ({selectedReceiptIds.length})
              </Button>
            </Stack>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search receipt no., amount or date..."
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: { sm: 400 },
                "& .MuiInputBase-input": {
                  fontSize: "0.85rem",
                },
              }}
            />

            <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
              Matching Receipts: {filteredReceipts.length}
            </Typography>
          </Stack>
          <Box sx={{ width: "100%" }}>
            <DataGrid
              rows={filteredReceipts}
              columns={columns}
              getRowId={(row) => String(row.fee_receipt_id)}
              loading={loading}
              autoHeight
              page={page}
              onPageChange={(newPage) => setPage(newPage)}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              disableSelectionOnClick
              hideFooter={filteredReceipts.length === 0}
              localeText={{
                noRowsLabel: loading
                  ? "Loading receipts..."
                  : searchText.trim()
                  ? "No matching receipts found."
                  : "No receipts found for this student.",
              }}
              sx={{
                border: "1px solid #e0e0e0",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#e3f2fd",
                  fontWeight: 700,
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
                "& .MuiDataGrid-cell": {
                  fontSize: "0.84rem",
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

FeeReceiptsView.propTypes = {
  selectedStudent: PropTypes.shape({
    student_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    student_rollno: PropTypes.string,
    student_roll_no: PropTypes.string,
    rollno: PropTypes.string,
    student_name: PropTypes.string,
    name: PropTypes.string,
  }),
};

FeeReceiptsView.defaultProps = {
  selectedStudent: null,
};
